import { getResponseStatus } from "@istari-frontend/shared";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@istari-frontend/ui";
import { useCallback, useEffect, useId, useState } from "react";
import { toast } from "sonner";

import { useSetConversationTranscript } from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { useChatSaveInfosec } from "~/ai-chat/feature/use-chat-save-infosec";
import { useChatSaveTarget } from "~/ai-chat/feature/use-chat-save-target";
import { useSaveChatResource } from "~/ai-chat/feature/use-save-chat-resource";
import type { ChatTranscriptItem } from "~/ai-chat/model/chat-message";
import { ChatClassifyFields } from "~/ai-chat/ui/chat-classify-fields";
import { ManageAccessModalContent } from "~/components/manage-access-dialog";
import { ControlTagMultiSelect } from "~/control-tags/feature/control-tag-multi-select";
import { ResourceType } from "~/enums";
import { useResource } from "~/resource/data-source/use-resource";

interface ChatShareModalProps {
  messages: ChatTranscriptItem[];
  defaultName: string;
  // The resource this chat is already saved as, or null to classify + save before sharing.
  initialResourceId: string | null;
  onClose: () => void;
}

// One share-focused modal. An already-saved chat opens the sharing view for the existing resource — i.e. the
// transcript as it was last saved; it does NOT upload a new revision of later messages. A never-saved chat
// names + classifies the transcript first, then this swaps to the sharing view for the new resource. Each
// step mounts its own Dialog, so the transition is an unmount/mount, not an in-place content swap.
export function ChatShareModal({ messages, defaultName, initialResourceId, onClose }: ChatShareModalProps) {
  const [resourceId, setResourceId] = useState<string | null>(initialResourceId);
  const { activeConversationId, setSavedTranscriptResourceId } = useAiChat();
  const { mutate: setTranscript } = useSetConversationTranscript();

  // The linked resource was deleted or access was revoked: drop the dead link and fall back to classify.
  const handleStale = useCallback(() => {
    setSavedTranscriptResourceId(null);
    if (activeConversationId) {
      setTranscript({ conversationId: activeConversationId, transcriptResourceId: null });
    }
    setResourceId(null);
  }, [activeConversationId, setSavedTranscriptResourceId, setTranscript]);

  if (resourceId === null) {
    return (
      <ChatShareClassifyStep messages={messages} defaultName={defaultName} onSaved={setResourceId} onCancel={onClose} />
    );
  }
  return <ChatShareAccessStep resourceId={resourceId} onClose={onClose} onStale={handleStale} />;
}

interface SavedChatShareModalProps {
  conversationId: string;
  // The resource the conversation's transcript was saved as.
  resourceId: string;
  onClose: () => void;
}

// Shares a saved conversation from its library link alone, without loading its transcript (it need not be the
// one on screen). With nothing to re-save, a dead link is cleared from that conversation and the modal closes
// instead of falling back to classify.
export function SavedChatShareModal({ conversationId, resourceId, onClose }: SavedChatShareModalProps) {
  const { mutate: setTranscript } = useSetConversationTranscript();

  const handleStale = useCallback(() => {
    setTranscript({ conversationId, transcriptResourceId: null });
    toast.error("This chat's saved copy is no longer available. Open the chat to share it again.");
    onClose();
  }, [conversationId, onClose, setTranscript]);

  return <ChatShareAccessStep resourceId={resourceId} onClose={onClose} onStale={handleStale} />;
}

// Names + classifies the transcript (name, infosec level, control tags) and saves it (to the chat's
// system/branch when it is open on one, else the library). Owns the form state and hooks so they don't
// mount when the modal opens straight to the sharing view.
function ChatShareClassifyStep({
  messages,
  defaultName,
  onSaved,
  onCancel,
}: {
  messages: ChatTranscriptItem[];
  defaultName: string;
  onSaved: (resourceId: string) => void;
  onCancel: () => void;
}) {
  const target = useChatSaveTarget();
  const destination = target.status === "ready" ? target : undefined;
  const { save, isSaving } = useSaveChatResource(destination);
  const { infosecEnabled, infosecLevels, defaultInfosecLevel } = useChatSaveInfosec();
  const [name, setName] = useState(defaultName);
  const [infosecLevelId, setInfosecLevelId] = useState<string | null>(null);
  const controlTagsId = useId();
  const [controlTagIds, setControlTagIds] = useState<string[]>([]);

  const trimmedName = name.trim();
  // `target.status === "loading"` also blocks, so a system chat can't slip into the library before placement
  // is known.
  const canContinue =
    trimmedName.length > 0 && infosecEnabled !== undefined && target.status !== "loading" && !isSaving;

  const handleContinue = () => {
    if (!canContinue) return;
    void save(trimmedName, messages, {
      infosecLevelId: infosecLevelId ?? undefined,
      saveToSystem: true,
      controlTagIds,
    }).then((id) => {
      if (id) onSaved(id);
    });
  };

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && !isSaving && onCancel()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Share chat</DialogTitle>
          <DialogDescription>
            {destination
              ? `Save this chat to ${destination.systemName} on ${destination.branch.name}, then choose who to share it with.`
              : "Save this chat to your library, then choose who to share it with."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <ChatClassifyFields
            name={name}
            onNameChange={setName}
            showInfosecLevel={infosecEnabled === true}
            infosecLevels={infosecLevels}
            defaultInfosecLevel={defaultInfosecLevel}
            infosecLevelId={infosecLevelId}
            onInfosecLevelChange={setInfosecLevelId}
          />
          <ControlTagMultiSelect
            id={controlTagsId}
            label={<span className="text-xs">Control tags</span>}
            value={controlTagIds}
            onChange={setControlTagIds}
            disabled={isSaving}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleContinue} disabled={!canContinue}>
            {isSaving ? "Saving…" : "Continue to sharing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Opens the sharing view for a saved resource, telling the user it's the last-saved copy. The link is an
// unvalidated pointer: a 403/404 on the resource means it was deleted or access was revoked, so recover via
// `onStale` instead of showing a dead access view. Other errors (5xx, network) fall through to
// ManageAccessModalContent, which renders its own titled loading/error states on the same resource.
function ChatShareAccessStep({
  resourceId,
  onClose,
  onStale,
}: {
  resourceId: string;
  onClose: () => void;
  onStale: () => void;
}) {
  const resourceQuery = useResource(resourceId);
  const status = resourceQuery.isError ? getResponseStatus(resourceQuery.error) : undefined;
  const isStale = status === 403 || status === 404;

  useEffect(() => {
    if (isStale) onStale();
  }, [isStale, onStale]);

  if (isStale) return null;

  const savedOn = resourceQuery.data?.updatedAt;
  const description = savedOn
    ? `Shares the copy saved on ${savedOn.toLocaleDateString()}. Messages added since aren't included — use Save to update it.`
    : undefined;

  return (
    <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
      <ManageAccessModalContent
        resourceType={ResourceType.Model}
        modelId={resourceId}
        title="Share chat"
        description={description}
        setModalOpen={(isOpen) => !isOpen && onClose()}
      />
    </Dialog>
  );
}
